import React from 'react';
import styled from 'styled-components';
import { FaBriefcase, FaGraduationCap } from 'react-icons/fa';

const experiences = [
  {
    id: 1,
    role: 'Lead Data Engineer',
    company: 'John Deere',
    duration: '09/2025 - Present',
    location: 'Pune, India',
    description: [
      'Developing a scalable data platform for processing and analyzing high-volume imagery data using AWS cloud infrastructure',
      'Designing and implementing cloud-native data solutions with AWS services including S3, Lambda, Step Functions, and SQS',
      'Building and optimizing data pipelines using Databricks for efficient large-scale image processing and analysis',
      'Implementing serverless architectures and event-driven workflows for cost-effective data processing',
      'Ensuring data quality and system reliability through comprehensive monitoring using CloudWatch and SNS',
      'Optimizing data storage solutions and query performance with PostgreSQL and Amazon Athena'
    ]
  },
  {
    id: 2,
    role: 'Consultant Data Engineer',
    company: 'Thoughtworks',
    duration: '04/2024 - 08/2025',
    location: 'Pune, India',
    description: [
      'Worked as a Data Engineer focusing on AWS services such as S3, EMR, Athena, Glue and PySpark',
      'Orchestrated data pipelines using AirFlow',
      'Ingested data from various RDBMS sources including PostgreSQL and Oracle',
      'Processed data using EMR serverless and loaded it into S3 in delta format',
      'Developed pipelines using Azure services including Azure Data Factory, ADLS, and Databricks',
      'Created Athena tables using Glue Crawler and Databricks tables for downstream teams',
      'Implemented data quality assurance using the Great Expectations framework'
    ]
  },
  {
    id: 3,
    role: 'Software Engineer',
    company: 'Xoriant Solutions',
    duration: '11/2020 - 03/2024',
    location: 'Pune, India',
    description: [
      'Worked on loading data into AWS S3 bucket and process it using Athena and Glue',
      'Performed and optimized rule checks on Big Data using pyspark and populated results in Hive or Oracle',
      'Performed rule check on data using python pandas and numpy library',
      'Created rules using Python Spacy Library and NLP concepts',
      'Developed system to answer user\'s questions regarding FRB regulatory instructions using GenAI tech',
      'Worked on creating ScheduleOBot using Airflow and Spacy'
    ]
  },
  {
    id: 4,
    role: 'Intern',
    company: 'DigitalBuzz Software',
    duration: '06/2019 - 07/2019',
    location: 'Remote',
    description: [
      'Developed a website for School Management System using PHP and HTML with MySQL database',
      'Worked on front-end and back-end parts of Student Module',
      'Designed data model for Student Module'
    ]
  }
];

const education = [
  {
    id: 1,
    degree: 'B.E. Computer Engineering',
    institution: 'Dr. DY Patil School of Engineering, Savitribai Phule Pune University',
    duration: '2016 - 2020'
  }
];

const Experience = () => {
  return (
    <ExperienceContainer id="experience">
      <div className="container">
        <h2>Experience</h2>
        <div className="timeline">
          {experiences.map((exp) => (
            <div className="timeline-item" key={exp.id}>
              <div className="timeline-icon">
                <FaBriefcase />
              </div>
              <div className="timeline-content">
                <h3>{exp.role}</h3>
                <div className="company">{exp.company}</div>
                <div className="duration">
                  {exp.duration} | {exp.location}
                </div>
                <ul>
                  {exp.description.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <h2 className="education-title">Education</h2>
        <div className="timeline">
          {education.map((edu) => (
            <div className="timeline-item" key={edu.id}>
              <div className="timeline-icon education">
                <FaGraduationCap />
              </div>
              <div className="timeline-content">
                <h3>{edu.degree}</h3>
                <div className="company">{edu.institution}</div>
                <div className="duration">
                  {edu.duration}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ExperienceContainer>
  );
};

const ExperienceContainer = styled.section`
  padding: 4rem 0;
  background-color: ${({ theme }) => theme.colors.light};

  h2 {
    text-align: center;
    margin-bottom: 2.5rem;
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.dark};
  }

  .education-title {
    margin-top: 3.5rem;
  }

  .timeline {
    position: relative;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1.5rem;
    
    @media (max-width: 768px) {
      padding: 0 1rem;
    }
  }

  .timeline::after {
    content: '';
    position: absolute;
    width: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    top: 0;
    bottom: 0;
    left: 2rem;
    margin-left: -1px;
  }

  .timeline-item {
    padding: 10px 0 30px 40px;
    position: relative;
    background-color: inherit;
    width: 100%;
    
    @media (max-width: 768px) {
      padding: 8px 0 25px 50px;
    }
  }

  .timeline-icon {
    position: absolute;
    left: 0;
    top: 15px;
    width: 40px;
    height: 40px;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  .timeline-icon.education {
    background: ${({ theme }) => theme.colors.secondary};
  }

  .timeline-content {
    padding: 16px 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
      padding: 14px 16px;
    }
  }

  .timeline-content h3 {
    margin: 0 0 5px 0;
    color: ${({ theme }) => theme.colors.primary};
  }

  .company {
    font-weight: 600;
    margin-bottom: 5px;
    color: ${({ theme }) => theme.colors.dark};
  }

  .duration {
    color: ${({ theme }) => theme.colors.gray};
    font-size: 0.9rem;
    margin-bottom: 10px;
  }

  ul {
    margin: 10px 0 0 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 6px;
    line-height: 1.5;
    font-size: 0.95rem;
    
    @media (max-width: 768px) {
      margin-bottom: 5px;
      font-size: 0.9rem;
    }
  }

  @media (max-width: 768px) {
    .timeline::after {
      left: 31px;
    }

    .timeline-item {
      padding-left: 70px;
      padding-right: 0;
    }
  }
`;

export default Experience;
